import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isHome = location.pathname === '/' || location.pathname === '';
  // "solid" = light header with dark text. Always solid when the mobile menu is
  // open so the dropdown has a real background (otherwise it overlaps the hero).
  const solid = scrolled || isHome === false || mobileOpen;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Close the menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const navItems = [
    { key: 'nav.services', href: '/services' },
    { key: 'nav.about', href: '/about' },
    { key: 'nav.pricing', href: '/pricing' },
    { key: 'nav.contact', href: '/contact' },
  ];

  const linkClasses = `text-sm tracking-[0.1em] uppercase font-label transition-colors duration-300 cursor-pointer whitespace-nowrap ${
    solid
      ? 'text-foreground-600 hover:text-primary-600'
      : 'text-white/75 hover:text-white'
  }`;

  const logoClasses = `font-heading text-xl md:text-2xl tracking-[0.15em] transition-colors duration-500 ${
    solid ? 'text-primary-700' : 'text-white'
  }`;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-24">
          <Link to="/" className="flex items-center gap-3 group cursor-pointer no-underline">
            <div className={`relative w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full border-2 transition-all duration-500 group-hover:scale-105 group-hover:shadow-lg ${
              solid
                ? 'bg-primary-500/10 border-primary-500/25 group-hover:bg-primary-500/20 group-hover:border-primary-400/40 group-hover:shadow-primary-500/10'
                : 'bg-white/10 border-white/25 group-hover:bg-white/20 group-hover:border-white/40 group-hover:shadow-white/10'
            }`}>
              <div className={`absolute inset-0 rounded-full bg-gradient-to-br transition-all duration-500 ${
                solid
                  ? 'from-primary-500/20 via-transparent to-transparent'
                  : 'from-white/20 via-transparent to-transparent'
              }`}></div>
              <i className={`text-base md:text-lg relative z-10 transition-all duration-500 ${
                solid
                  ? 'text-primary-600 group-hover:text-primary-500'
                  : 'text-white group-hover:text-white'
              } ri-heart-pulse-line`}></i>
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`font-heading text-xl md:text-2xl tracking-[0.12em] font-extrabold transition-colors duration-500 ${
                solid ? 'text-foreground-900' : 'text-white'
              }`}>
                ESTEVA
              </span>
              <span className={`w-px h-4 self-center transition-colors duration-500 ${
                solid ? 'bg-foreground-300' : 'bg-white/25'
              }`}></span>
              <span className={`font-label text-[11px] md:text-xs tracking-[0.25em] uppercase font-light transition-colors duration-500 ${
                solid ? 'text-foreground-500' : 'text-white/55'
              }`}>
                CLINIC
              </span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-6 lg:gap-9">
            {navItems.map((item) => (
              <Link
                key={item.key}
                to={item.href}
                className={linkClasses}
              >
                {t(item.key)}
              </Link>
            ))}
            <Link
              to="/team"
              className={`px-6 py-2.5 rounded-full text-sm tracking-[0.1em] uppercase font-label transition-all duration-300 cursor-pointer whitespace-nowrap no-underline ${
                solid
                  ? 'border border-primary-300 text-primary-600 hover:bg-primary-50'
                  : 'border border-white/30 text-white hover:bg-white/10'
              }`}
            >
              {t('nav.team')}
            </Link>
            <Link
              to="/booking"
              className={`ml-1 px-6 py-2.5 rounded-full text-sm tracking-[0.1em] uppercase font-label transition-all duration-300 cursor-pointer whitespace-nowrap no-underline ${
                solid
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-white text-primary-700 hover:bg-white/90'
              }`}
            >
              {t('nav.booking')}
            </Link>
            <div className={solid ? 'text-foreground-600' : 'text-white/80'}>
              <LanguageSwitcher variant={solid ? 'light' : 'dark'} />
            </div>
          </nav>

          <div className="flex md:hidden items-center gap-3">
            <div className={solid ? 'text-foreground-600' : 'text-white/80'}>
              <LanguageSwitcher variant={solid ? 'light' : 'dark'} />
            </div>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`w-8 h-8 flex items-center justify-center cursor-pointer ${
                solid ? 'text-foreground-800' : 'text-white'
              }`}
            >
              <i className={`text-lg ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-5 border-t border-background-200/70 bg-background-50 max-h-[calc(100dvh-4rem)] overflow-y-auto -mx-6 px-6">
            <nav className="flex flex-col gap-0.5 pt-4 px-2">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 text-sm tracking-[0.1em] uppercase font-label rounded-lg transition-all ${
                    solid
                      ? 'text-foreground-600 hover:text-primary-600 hover:bg-background-100'
                      : 'text-white/85 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {t(item.key)}
                </Link>
              ))}
              <div className="mt-3 pt-3 border-t border-black/5 space-y-2">
                <Link
                  to="/team"
                  onClick={() => setMobileOpen(false)}
                  className="block px-5 py-2.5 rounded-full text-xs tracking-[0.12em] uppercase font-label border border-primary-300 text-primary-600 hover:bg-primary-50 transition-colors text-center cursor-pointer no-underline"
                >
                  {t('nav.team')}
                </Link>
                <Link
                  to="/booking"
                  onClick={() => setMobileOpen(false)}
                  className="block px-5 py-3 rounded-full text-xs tracking-[0.12em] uppercase font-label bg-primary-500 text-white hover:bg-primary-600 transition-colors text-center cursor-pointer no-underline"
                >
                  {t('nav.booking')}
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}